import CreateActionButton from '@/components/patterns/create-action-button'
import {
  getIndexPaginationMeta,
  IndexDataTable,
  IndexTableClearFiltersButton,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import dayjs from '@/lib/dayjs'
import { Link } from '@tanstack/react-router'
import { CalendarDays, Image, Plus } from 'lucide-react'

const ALL_OCCASIONS_VALUE = 'all'

const statusLabels = {
  archived: 'Archived',
  draft: 'Draft',
  published: 'Published',
}

const statusVariants = {
  archived: 'muted',
  draft: 'outline',
  published: 'success',
}

const columns = [
  {
    accessorKey: 'name',
    header: 'Design',
    meta: {
      label: 'Design',
      kind: 'primary',
      preferredWidth: '22rem',
      minWidth: '17rem',
      sortable: { asc: 'name', desc: 'name_desc' },
    },
    cell: ({ row }) => {
      const design = row.original
      const media = design.published_revision?.media ?? design.draft_photo_asset?.latest_media

      return (
        <div className="flex min-w-0 items-center gap-3">
          <div className="size-12 shrink-0 overflow-hidden rounded-lg bg-muted">
            {media?.url ? <img src={media.url} alt="" className="size-full object-cover" /> : null}
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium">{design.name}</div>
            <Typography variant="caption" className="block">
              Revision {design.published_revision?.revision ?? '—'}
            </Typography>
          </div>
        </div>
      )
    },
  },
  {
    id: 'occasion',
    accessorFn: (design) => design.seasonal_tag?.label ?? design.occasion?.label ?? '',
    header: 'Occasion / season',
    meta: { label: 'Occasion / season', kind: 'text', minWidth: '10rem' },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    meta: { label: 'Status', kind: 'badge' },
    cell: ({ row }) => (
      <Badge variant={statusVariants[row.original.status] ?? 'outline'}>
        {statusLabels[row.original.status] ?? row.original.status}
      </Badge>
    ),
  },
  {
    accessorKey: 'usage_count',
    header: 'Using it',
    meta: {
      label: 'Using it',
      kind: 'number',
      align: 'end',
      sortable: { asc: 'usage_asc', desc: 'usage_desc', defaultDirection: 'desc' },
    },
  },
  {
    accessorKey: 'live_impact_count',
    header: 'Live now',
    meta: {
      label: 'Live now',
      kind: 'number',
      align: 'end',
      sortable: { asc: 'live_asc', desc: 'live_desc', defaultDirection: 'desc' },
    },
  },
  {
    accessorKey: 'updated_at',
    header: 'Updated',
    meta: {
      label: 'Updated',
      kind: 'date',
      sortable: { asc: 'updated_asc', desc: 'updated_desc', defaultDirection: 'desc' },
    },
    cell: ({ row }) =>
      row.original.updated_at ? dayjs(row.original.updated_at).format('M/D/YYYY') : '—',
  },
]

export function SeasonalOccasionFilterSelect({ occasions, occasionId, onOccasionChange }) {
  const value = occasionId ? String(occasionId) : ALL_OCCASIONS_VALUE

  return (
    <Select
      value={value}
      onValueChange={(nextValue) =>
        onOccasionChange(nextValue === ALL_OCCASIONS_VALUE ? undefined : Number(nextValue))
      }
    >
      <SelectTrigger
        aria-label="Filter seasonal designs by occasion or season"
        selected={Boolean(occasionId)}
        className="w-fit min-w-44"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_OCCASIONS_VALUE}>
          <CalendarDays className="size-4" />
          All occasions and seasons
        </SelectItem>
        {occasions.map((occasion) => (
          <SelectItem key={occasion.id} value={String(occasion.id)}>
            <CalendarDays className="size-4" />
            {occasion.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function SeasonalDesignIndexTable({
  response,
  search,
  occasions = [],
  occasionId,
  sort,
  page,
  pageSize,
  isFetching,
  isPending,
  onSearchChange,
  onOccasionChange,
  onClearFilters,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  isReadOnly,
  organizationId,
}) {
  const path = usePathHelper()
  const designs = response?.data ?? []
  const paginationMeta = getIndexPaginationMeta(response, page, pageSize)
  const hasActiveFilters = Boolean(search.trim() || occasionId)

  return (
    <div className="space-y-4">
      <IndexDataTable
        data={designs}
        columns={columns}
        sort={sort}
        onSortChange={onSortChange}
        getRowId={(design) => String(design.id)}
        rowLink={(design) =>
          path.seasonalContent.show(design.id).using({ orgId: organizationId }).link
        }
        isLoading={isPending}
        toolbar={
          <IndexTableToolbar
            primary={
              <IndexTableSearchBox
                value={search}
                onCommit={onSearchChange}
                countText={`${paginationMeta.total} design${paginationMeta.total === 1 ? '' : 's'}`}
                isFetching={isFetching}
                placeholder="Search seasonal designs..."
              />
            }
            secondary={
              <>
                {hasActiveFilters ? (
                  <IndexTableClearFiltersButton onClick={onClearFilters} />
                ) : null}
                <SeasonalOccasionFilterSelect
                  occasions={occasions}
                  occasionId={occasionId}
                  onOccasionChange={onOccasionChange}
                />
              </>
            }
          />
        }
        emptyState={
          <div className="grid min-h-72 place-items-center p-8 text-center">
            <div className="grid max-w-md justify-items-center gap-3">
              <span className="inline-grid size-14 place-items-center rounded-xl bg-muted text-muted-foreground">
                <Image className="size-7" />
              </span>
              <Typography variant="h3">
                {hasActiveFilters ? 'No matching seasonal designs' : 'No seasonal designs yet'}
              </Typography>
              <Typography variant="supporting">
                {hasActiveFilters
                  ? 'Try another name, occasion, or season.'
                  : 'Pair a banner, finished wording, and a layout, then publish the complete look for florist websites.'}
              </Typography>
              {!isReadOnly && !hasActiveFilters ? (
                <CreateActionButton asChild>
                  <Link {...path.seasonalContent.create().using({ orgId: organizationId }).link}>
                    <Plus className="size-4" /> Create design
                  </Link>
                </CreateActionButton>
              ) : null}
            </div>
          </div>
        }
        tableClassName="text-sm"
      />
      <IndexTablePagination
        meta={paginationMeta}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  )
}
